import { useState } from "react";
import { genresList, regionsList, yearsList, ratingOptions } from "@/mocks/movies";

export interface FilterState {
  types: string[];
  genres: string[];
  regions: string[];
  years: number[];
  minRating: number;
}

export type SortKey = "latest" | "rating" | "hot";

export const emptyFilters: FilterState = {
  types: [],
  genres: [],
  regions: [],
  years: [],
  minRating: 0,
};

interface FilterBarProps {
  filters: FilterState;
  sort: SortKey;
  resultCount: number;
  onToggle: (key: keyof FilterState, value: string | number) => void;
  onSetRating: (value: number) => void;
  onSortChange: (sort: SortKey) => void;
  onReset: () => void;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "rating", label: "评分" },
  { key: "hot", label: "热门" },
];

function Chip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
      }`}
    >
      {label}
    </button>
  );
}

function FilterGroups({ filters, onToggle, onSetRating }: Pick<FilterBarProps, "filters" | "onToggle" | "onSetRating">) {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-3">作品类型</h4>
        <div className="flex flex-wrap gap-2">
          {["电影", "电视剧"].map((t) => (
            <Chip key={t} active={filters.types.includes(t)} label={t} onClick={() => onToggle("types", t)} />
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-3">题材分类</h4>
        <div className="flex flex-wrap gap-2">
          {genresList.map((g) => (
            <Chip key={g} active={filters.genres.includes(g)} label={g} onClick={() => onToggle("genres", g)} />
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-3">地区</h4>
        <div className="flex flex-wrap gap-2">
          {regionsList.map((r) => (
            <Chip key={r} active={filters.regions.includes(r)} label={r} onClick={() => onToggle("regions", r)} />
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-3">年份</h4>
        <div className="flex flex-wrap gap-2">
          {yearsList.map((y) => (
            <Chip key={y} active={filters.years.includes(y)} label={`${y}`} onClick={() => onToggle("years", y)} />
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-heading text-sm font-bold text-foreground-900 mb-3">评分</h4>
        <div className="flex flex-wrap gap-2">
          {ratingOptions.map((opt) => (
            <Chip
              key={opt.value}
              active={filters.minRating === opt.value}
              label={opt.label}
              onClick={() => onSetRating(filters.minRating === opt.value ? 0 : opt.value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FilterBar({
  filters,
  sort,
  resultCount,
  onToggle,
  onSetRating,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeCount =
    filters.types.length + filters.genres.length + filters.regions.length + filters.years.length + (filters.minRating ? 1 : 0);

  return (
    <>
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-lg font-black text-foreground-950">筛选</h3>
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置
            </button>
          </div>
          <FilterGroups filters={filters} onToggle={onToggle} onSetRating={onSetRating} />
        </div>
      </aside>

      <div className="lg:hidden w-full">
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-background-200 bg-background-50">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            筛选
            {activeCount > 0 && (
              <span className="ml-0.5 px-1.5 rounded-full bg-accent-500 text-primary-950 text-xs font-bold">{activeCount}</span>
            )}
          </button>
          <div className="flex items-center gap-1 overflow-x-auto">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onSortChange(opt.key)}
                className={`px-3 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap ${
                  sort === opt.key ? "bg-accent-100 text-accent-800" : "text-foreground-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {drawerOpen && (
          <div className="fixed inset-0 z-[60] flex">
            <div className="absolute inset-0 bg-primary-950/50" onClick={() => setDrawerOpen(false)}></div>
            <div className="relative ml-auto w-[85%] max-w-sm h-full bg-background-50 p-5 overflow-y-auto animate-fade-in">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-black text-foreground-950">筛选条件</h3>
                <button
                  type="button"
                  aria-label="关闭"
                  onClick={() => setDrawerOpen(false)}
                  className="w-9 h-9 rounded-md bg-background-100 flex items-center justify-center text-foreground-800 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <FilterGroups filters={filters} onToggle={onToggle} onSetRating={onSetRating} />
              <div className="flex items-center gap-3 mt-8">
                <button
                  type="button"
                  onClick={onReset}
                  className="flex-1 py-3 rounded-md border border-background-300 text-sm font-medium text-foreground-800 cursor-pointer whitespace-nowrap"
                >
                  重置
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="flex-1 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-bold cursor-pointer whitespace-nowrap"
                >
                  查看 {resultCount} 部
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}